// PATH: src/components/documents/lessonCheck.ts
/**
 * Un chapitre leçon par leçon : mise en forme pure de
 * `GET /v1/documents/:id/lessons` et du bilan `lessons` de
 * `POST /v1/documents/:id/objectives`.
 *
 * Les objectifs sont déduits du cours et rattachés à leur leçon par la
 * structure (décision du 10/10/2026) ; le serveur classe chaque leçon
 * (prête / faible / vide). Ici on ne fait que compter et dire, sans jamais
 * lire le texte.
 */
import type {
  DocumentObjective,
  LessonObjective,
  LessonStatus,
  LessonsReport,
  LessonsReportSummary,
  LessonsRunSummary,
  ObjectivesResponse,
} from "../../api/objectives/objectives";
import type { PillTone } from "../ui/StatusPill";

/** « 1 leçon », « 2 leçons » ; 0 et 1 au singulier. */
export const countLabel = (n: number, one: string, many: string): string => `${n} ${n > 1 ? many : one}`;

export const LESSON_STATUS: Record<LessonStatus, { label: string; tone: PillTone; hint: string }> = {
  ready: { label: "Prête", tone: "success", hint: "Au moins 2 objectifs, tous avec une base de faits." },
  weak: {
    label: "Faible",
    tone: "warning",
    hint: "Des objectifs, mais moins de 2, ou certains sans base de faits.",
  },
  empty: { label: "Vide", tone: "error", hint: "Aucun objectif : l'élève ne travaillera pas cette leçon." },
};

/** Statut inconnu (serveur plus récent) : traité comme faible plutôt que d'échouer. */
export const lessonStatusInfo = (status: string) =>
  LESSON_STATUS[(status as LessonStatus) in LESSON_STATUS ? (status as LessonStatus) : "weak"];

/**
 * Raison du statut, tirée des comptes : « aucun objectif », « 1 objectif
 * (2 attendus) », « 5 objectifs, dont 2 sans faits », « 4 objectifs, tous
 * avec faits ».
 */
export const lessonReason = (status: string, objectives: number, withFacts: number): string => {
  if (objectives === 0) return "aucun objectif";
  const withoutFacts = Math.max(0, objectives - withFacts);
  if (status === "ready") return `${countLabel(objectives, "objectif", "objectifs")}, tous avec faits`;
  const count = countLabel(objectives, "objectif", "objectifs");
  if (objectives < 2) return `${count} (2 attendus)${withoutFacts > 0 ? ", sans faits" : ""}`;
  return withoutFacts > 0 ? `${count}, dont ${withoutFacts} sans faits` : count;
};

export interface LessonRow {
  leconRef: string;
  ordre: number;
  titre: string;
  passages: number;
  status: LessonStatus;
  label: string;
  tone: PillTone;
  reason: string;
  objectives: LessonObjective[];
  withFacts: number;
}

/** Les leçons dans l'ordre du manifeste, avec statut lisible et raison. */
export const buildLessonRows = (report: LessonsReport): LessonRow[] =>
  [...(report.lessons ?? [])]
    .sort((a, b) => a.ordre - b.ordre)
    .map((l) => {
      const objectives = [...(l.objectives ?? [])].sort((a, b) => a.ordinal - b.ordinal);
      const withFacts = objectives.filter((o) => o.has_facts).length;
      const info = lessonStatusInfo(l.status);
      return {
        leconRef: l.lecon_ref,
        ordre: l.ordre,
        titre: l.titre,
        passages: l.passages,
        status: l.status,
        label: info.label,
        tone: info.tone,
        reason: lessonReason(l.status, objectives.length, withFacts),
        objectives,
        withFacts,
      };
    });

/** « 11 leçons prêtes sur 12 · 70 objectifs, 68 avec faits ». */
export const formatLessonsSummary = (s: Pick<LessonsReportSummary, "lessons" | "ready" | "objectives" | "objectives_with_facts">): string =>
  `${countLabel(s.ready, "leçon prête", "leçons prêtes")} sur ${s.lessons} · ` +
  `${countLabel(s.objectives, "objectif", "objectifs")}, ${s.objectives_with_facts} avec faits`;

/** « 2 leçons vides · 1 leçon faible » ; vide si tout est prêt. */
export const formatLessonsAttention = (s: { empty: number; weak: number }): string =>
  [
    s.empty > 0 ? countLabel(s.empty, "leçon vide", "leçons vides") : null,
    s.weak > 0 ? countLabel(s.weak, "leçon faible", "leçons faibles") : null,
  ]
    .filter(Boolean)
    .join(" · ");

/** Couleur d'un résumé : une leçon vide est grave, une faible à reprendre. */
export const summaryTone = (s: { empty: number; weak: number }): "error" | "warning" | "success" =>
  s.empty > 0 ? "error" : s.weak > 0 ? "warning" : "success";

/* ---------- Bilan d'une extraction ---------- */

export interface LessonsRunView {
  severity: "success" | "warning" | "error";
  /** « 11 leçons prêtes sur 12 (1 faible) · 64 objectifs : 30 repris, 34 créés, 1 orphelin. » */
  headline: string;
  /** Ce que ça coûte aux élèves : orphelins, travail éditorial emporté. */
  details: string[];
  /** Leçons à reprendre (faibles ou vides), dans l'ordre, avec leur constat. */
  toReview: { leconRef: string; titre: string; label: string; tone: PillTone; reason: string }[];
}

/**
 * Bilan d'une extraction par leçon. `top` porte les comptes du document
 * entier (travail éditorial emporté par la cascade des orphelins).
 */
export const formatLessonsRun = (
  run: LessonsRunSummary,
  top?: Pick<ObjectivesResponse, "orphaned_overrides" | "orphaned_reports"> | null,
): LessonsRunView => {
  const attention = formatLessonsAttention(run);
  const headline =
    `${countLabel(run.ready, "leçon prête", "leçons prêtes")} sur ${run.lessons}` +
    (attention ? ` (${attention})` : "") +
    ` · ${countLabel(run.objectives, "objectif", "objectifs")} : ` +
    `${run.reused} repris, ${countLabel(run.created, "créé", "créés")}, ` +
    `${countLabel(run.orphaned, "orphelin", "orphelins")}.`;

  const details: string[] = [];
  if (run.reused > 0) {
    details.push(
      `${countLabel(run.reused, "objectif repris", "objectifs repris")} : même identifiant, la progression des élèves est conservée.`,
    );
  }
  if (run.orphaned > 0) {
    details.push(
      `${countLabel(run.orphaned, "objectif orphelin", "objectifs orphelins")} : ` +
        `${run.orphaned > 1 ? "ils n'ont" : "il n'a"} plus d'équivalent dans la nouvelle extraction, ` +
        "la progression des élèves sur " +
        `${run.orphaned > 1 ? "ces objectifs" : "cet objectif"} est perdue.`,
    );
  }
  const overrides = top?.orphaned_overrides ?? 0;
  const reports = top?.orphaned_reports ?? 0;
  if (overrides + reports > 0) {
    details.push(
      `${countLabel(overrides, "correction éditoriale", "corrections éditoriales")} et ` +
        `${countLabel(reports, "signalement", "signalements")} d'élève emportés avec les orphelins.`,
    );
  }

  const toReview = [...(run.items ?? [])]
    .filter((i) => i.status !== "ready")
    .sort((a, b) => a.ordre - b.ordre)
    .map((i) => {
      const info = lessonStatusInfo(i.status);
      return {
        leconRef: i.lecon_ref,
        titre: i.titre,
        label: info.label,
        tone: info.tone,
        reason: i.issue || lessonReason(i.status, i.objectives, i.objectives_with_facts),
      };
    });

  const severity =
    run.empty > 0 ? "error" : run.weak > 0 || run.orphaned > 0 || overrides + reports > 0 ? "warning" : "success";
  return { severity, headline, details, toReview };
};

/* ---------- Objectifs groupés par leçon ---------- */

export interface LessonGroup<T> {
  /** `lecon_ref`, ou "" pour les objectifs sans leçon du manifeste. */
  key: string;
  ordre: number | null;
  titre: string;
  status: LessonStatus | null;
  objectives: T[];
}

/**
 * Range des objectifs sous leur leçon, dans l'ordre du manifeste. Les
 * objectifs sans leçon connue tombent dans un dernier groupe (clé ""). Sans
 * leçons (document qui n'est pas un chapitre importé) : null, la liste reste
 * plate. L'ordre des objectifs dans un groupe est celui reçu.
 */
export const groupByLesson = <T extends Pick<DocumentObjective, "lecon_ref">>(
  objectives: T[],
  report: LessonsReport | null | undefined,
): LessonGroup<T>[] | null => {
  const lessons = report?.lessons ?? [];
  if (lessons.length === 0) return null;
  const groups = [...lessons]
    .sort((a, b) => a.ordre - b.ordre)
    .map<LessonGroup<T>>((l) => ({ key: l.lecon_ref, ordre: l.ordre, titre: l.titre, status: l.status, objectives: [] }));
  const byRef = new Map(groups.map((g) => [g.key, g]));
  const orphans: LessonGroup<T> = { key: "", ordre: null, titre: "", status: null, objectives: [] };
  objectives.forEach((o) => (byRef.get(o.lecon_ref ?? "") ?? orphans).objectives.push(o));
  return orphans.objectives.length ? [...groups, orphans] : groups;
};
